<template>
  <div id="backtester-results">
    <v-card v-if="!results">
      <v-card-text class="text-body-2">
        Backtest not run yet.
      </v-card-text>
    </v-card>

    <v-container
      v-else
      fluid
      grid-list-xl
      pa-0
    >
      <!-- Buttons: Excel, Start/Stop tracking -->
      <v-row>
        <v-col cols="12">
          <v-btn
            :small="$vuetify.breakpoint.xsOnly"
            :href="`/generated_backtesters/${results.excel_file}`"
            target="_blank"
            color="primary"
            class="ml-0"
          >
            Download Backtest Data to Excel
          </v-btn>

          <!-- Start tracking -->
          <v-dialog
            v-if="user"
            v-model="trackingStartDialog"
            :max-width="350"
            persistent
          >
            <template v-slot:activator="{ on }">
              <v-btn
                :small="$vuetify.breakpoint.xsOnly"
                color="primary"
                class="ml-0"
                v-on="on"
              >
                <span v-if="criteria.backtest_tracking_id">Copy Backtest</span>
                <span v-else>Start Tracking</span>
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                <v-toolbar-title>
                  <span v-if="criteria.backtest_tracking_id">Copy Backtest</span>
                  <span v-else>Start Tracking</span>
                </v-toolbar-title>
              </v-card-title>
              <v-card-text>
                <v-form
                  ref="trackingForm"
                  v-model="validTrackingDescription"
                  lazy-validation
                  @submit.prevent="trackingStart()"
                >
                  <v-text-field
                    ref="backtestDescriptionField"
                    v-model="criteria.backtest_tracking_description"
                    :rules="[v => !!v || 'Backtest description is required']"
                    label="Backtest description"
                    @keyup.enter="trackingStart()"
                  />
                </v-form>
              </v-card-text>
              <v-card-actions class="px-4">
                <v-spacer />
                <v-btn
                  text
                  @click="cancelTrackingStart()"
                >
                  Cancel
                </v-btn>
                <v-btn
                  :loading="loadingTracking"
                  :disabled="loadingTracking || !validTrackingDescription"
                  depressed
                  color="primary"
                  @click="trackingStart()"
                >
                <span v-if="criteria.backtest_tracking_id">Copy Backtest</span>
                <span v-else>Start Tracking</span>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-btn
            v-if="1==2 && user && criteria.backtest_tracking_id"
            :small="$vuetify.breakpoint.xsOnly"
            color="primary"
            class="ml-0"
            @click="UpdateBacktestTracking(criteria.backtest_tracking_id)"
          >
            <span>Update Alert</span>
          </v-btn>
          <!-- Stop tracking -->
          <v-dialog
            v-if="user && criteria.backtest_tracking_id"
            v-model="trackingStopDialog"
            :max-width="350"
            persistent
          >
            <template v-slot:activator="{ on }">
              <v-btn
                :small="$vuetify.breakpoint.xsOnly"
                color="primary"
                class="ml-0"
                v-on="on"
              >
                Stop tracking
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                <v-toolbar-title>
                  Stop tracking this backtest
                </v-toolbar-title>
              </v-card-title>
              <v-card-text class="mb-2">
                Delete this Backtest from your records?
              </v-card-text>
              <v-card-actions class="px-4">
                <v-spacer />
                <v-btn
                  text
                  @click="trackingStopDialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  :loading="loadingTracking"
                  :disabled="loadingTracking"
                  depressed
                  color="primary"
                  @click="trackingStop()"
                >
                  Stop tracking
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>

      <!-- Timeframe, Moving averages, Index selection, Backtest chart -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <!-- Timeframe, Index selection, Moving averages -->
            <v-card-text>
              <v-container
                fluid
                grid-list-xl
                pa-0
              >
                <v-row align="center">
                  <!-- Indices & Moving averages -->
                  <v-col
                    cols="12"
                    xl="8"
                    order-xl="2"
                  >
                    <v-row
                      align="center"
                      justify="end"
                    >
                      <!-- Index selection -->
                      <v-col
                        order-xl="2"
                      >
                        <v-row align="center">
                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <v-select
                              v-model="chart.xAxis"
                              attach
                              :items="chartIntervalItems"
                              label="Interval"
                              item-text="description"
                              return-object
                              hide-details
                              menu-props="offsetY"
                              class="ma-0"
                              style="max-width: 150px"
                              @change="refresh()"
                            />
                          </v-col>
                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <v-select
                              v-model="chart.comparisonIndex"
                              attach
                              :items="indices"
                              label="Comparison index"
                              item-text="description"
                              return-object
                              hide-details
                              menu-props="offsetY"
                              class="ma-0"
                              style="max-width: 300px"
                              @change="refresh()"
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Moving averages -->
                      <v-col
                        :mr-4="$vuetify.breakpoint.xlOnly"
                        class="shrink"
                        order-xl="1"
                      >
                        <v-btn
                          outlined
                          small
                          class="ma-0"
                          @click="movingAveragesDialog = true"
                        >
                          Configure moving averages
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <!-- Timeframe -->
                  <v-col
                    cols="12"
                    xl="4"
                    order-xl="1"
                  >
                    <v-btn-toggle
                      v-model="timeframe"
                      :class="{ 'justify-space-between': $vuetify.breakpoint.xsOnly }"
                      mandatory
                      class="elevation-0 transparent"
                      @change="refresh()"
                    >
                      <v-btn
                        v-for="item in timeframeItems"
                        :key="item.value"
                        :value="item"
                        :class="{ 'px-3': $vuetify.breakpoint.smAndUp }"
                        large
                        text
                      >
                        {{ item.description }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <!-- Subscription call to action -->
            <v-card-text
              v-if="!$store.state.user.isSubscriber"
              class="pa-6 dark-blue white--text"
            >
              <h5 class="text-h5 mb-2">
                Want to backtest using all available 13F history?
              </h5>
              <div>
                As a non-subscriber you are limited to the past two years of filing data. <a
                  style="color:#FFF033;"
                  href="/pricing"
                  target="_blank"
                >Subscribe now</a> and backtest using 13F holdings data as far back as 2001.
              </div>
            </v-card-text>

            <!-- Backtest Chart -->
            <v-card-text>
              <div id="backtest_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Backtest Table -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <v-data-table
                id="backtest-table"
                :headers="backtestTableHeaders"
                :items="backtestTableItems"
                hide-default-footer
              >
                <template
                  slot="item"
                  slot-scope="props"
                >
                  <tr>
                    <td>{{ props.item['name'] }}</td>
                    <td :class="[ props.item['YTD'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['YTD']) }}
                    </td>
                    <td :class="[ props.item['1Y'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['1Y']) }}
                    </td>
                    <td :class="[ props.item['2Y'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['2Y']) }}
                    </td>
                    <td :class="[ props.item['3Y'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['3Y']) }}
                    </td>
                    <td :class="[ props.item['5Y'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['5Y']) }}
                    </td>
                    <td :class="[ props.item['7Y'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['7Y']) }}
                    </td>
                    <td :class="[ props.item['10Y'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['10Y']) }}
                    </td>
                    <td :class="[ props.item['Total Return'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['Total Return']) }}
                    </td>
                    <td :class="[ props.item['Annualized'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['Annualized']) }}
                    </td>
                    <td :class="[ props.item['performance_three_year_annualized'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['performance_three_year_annualized']) }}
                    </td>
                    <td :class="[ props.item['performance_five_year_annualized'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['performance_five_year_annualized']) }}
                    </td>
                    <td :class="[ props.item['Std Dev'] > 0 ? 'green--text' : 'red--text']">
                      {{ percent(props.item['Std Dev']) }}
                    </td>
                    <td>{{ percent(props.item['Sortino']) }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Current Holdings Rebalanced -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text class="pa-6 dark-blue white--text">
              <h2
                class="text-h5 mb-2 d-inline-flex align-center"
                style="color:#fff"
              >
                <img
                  src="https://d27mjrcvcy56qq.cloudfront.net/images/ico_graph.png"
                  class="mr-2"
                >
                Current Holdings Rebalanced on {{ results.ending_period.start_date }}
              </h2>
              <div class="mb-4">
                Click the "Download Backtest Data to Excel" button above to see all stock activity
              </div>
              <v-alert type="info">
                Stock prices used are the "total returns" stock price and will include dividends, spinoffs, splits, and other adjustments. Because of this, stock prices shown here will likely not match a stock's actual closing stock price on the same date.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Stocks Table & Ending Sector Allocation chart -->
      <v-row mb-2>
        <!-- Stocks Table -->
        <v-col
          cols="12"
          xl="6"
        >
          <v-card>
            <v-card-text>
              <v-data-table
                id="backtest-stocks-table"
                :headers="stocksTableHeaders"
                :items="stocksTableItems"
                disable-pagination
                hide-default-footer
              >
                <template
                  slot="item"
                  slot-scope="props"
                >
                  <!-- Default row -->
                  <tr v-if="props.item.rowType === 'default'">
                    <td>
                      <a
                        :href="`/stock/${props.item.stock_permalink}`"
                        :title="props.item.stock_name"
                        class="font-weight-bold"
                        target="_blank"
                        v-text="props.item.ticker"
                      />
                      {{ props.item.stock_name }}
                    </td>
                    <td>{{ !props.item.shares ? 'Sold All' : percent(props.item.percent_of_portfolio * 100.0) }}</td>
                    <td :class="[ GetTransactionTotalReturn(props.item) > 0 ? 'green--text' : 'red--text']">
                      {{
                        props.item.is_hedge
                          ? currency(starting_record.hedging_gain)
                          : percent(GetTransactionTotalReturn(props.item))
                      }}
                    </td>
                    <td>{{ props.item.original_purchase_date }}</td>
                  </tr>

                  <!-- Exception: results.ending_period.hedge -->
                  <tr v-if="props.item.rowType === 'endingPeriodHedge'">
                    <td>{{ results.ending_period.hedge.stock_name }}</td>
                    <td />
                    <td :class="[ starting_record.hedging_gain > 0 ? 'green--text' : 'red--text']">
                      {{ percent(GetTransactionTotalReturn(results.ending_period.hedge)) }}
                    </td>
                    <td>{{ results.ending_period.hedge.original_purchase_date }}</td>
                  </tr>

                  <!-- Exception: ending_hedged_transaction && !ending_hedged_transaction.is_sold -->
                  <tr v-if="props.item.rowType === 'endingHedgedTransaction'">
                    <td>*** Currently using {{ ending_hedged_transaction.stock_name }} ***</td>
                    <td />
                    <td :class="[ starting_record.hedging_gain > 0 ? 'green--text' : 'red--text']">
                      {{ percent(GetTransactionTotalReturn(ending_hedged_transaction)) }}
                    </td>
                    <td>{{ ending_hedged_transaction.original_purchase_date }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ending Sector Allocation chart -->
        <v-col
          cols="12"
          xl="6"
        >
          <v-card>
            <v-card-text>
              <div id="backtest_sector_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Moving Averages dialog -->
    <v-dialog
      v-model="movingAveragesDialog"

      persistent
      width="600px"
    >
      <v-card>
        <v-card-title class="text-h6 primary white--text">
          Configure Moving Averages
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="maToAdd"
            :rules="[
              value => !moving_averages.some(ma => ma.months === value) || `${value} month moving average already added`
            ]"
            type="number"
            placeholder="Add an X month moving average"
            solo
            class="mb-2"
            @keydown.enter="AddMovingAverage()"
          >
            <v-icon
              slot="prepend-inner"
              color="success"
              @click="AddMovingAverage()"
            >
              add
            </v-icon>
          </v-text-field>
          <v-card v-if="moving_averages.length > 0">
            <v-list class="py-0">
              <template v-for="(ma, i) in moving_averages">
                <v-divider
                  v-if="i !== 0"
                  :key="`${i}-divider`"
                />

                <v-list-item :key="`${i}-${ma.description}`">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ ma.description }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      icon
                      @click="DeleteMovingAverage(ma)"
                    >
                      <v-icon color="error">
                        close
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-list>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="CloseMovingAverageDialog()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Modules
import api from '@/store/api'
import Highcharts from 'highcharts'
import last from 'lodash/last' // returns the last element of a given array
import { mapState } from 'vuex'
import { mapFields } from 'vuex-map-fields'

// Mixins
import FormattersMixin from '@/mixins/formatters'

export default {
  mixins: [FormattersMixin],

  data: () => ({
    movingAveragesDialog: false,
    trackingStartDialog: false,
    trackingStopDialog: false,
    validTrackingDescription: false,
    loadingTracking: false,
    results: null,
    timeframe: null,
    timeframeItems: [
      {
        description: 'YTD',
        value: 1,
        requires_subscription: false,
        time: 1
      },
      {
        description: '1y',
        value: 2,
        requires_subscription: false,
        time: 12
      },
      {
        description: '2y',
        value: 8,
        requires_subscription: false,
        time: 24
      },
      {
        description: '3y',
        value: 3,
        requires_subscription: true,
        time: 36
      },
      {
        description: '5y',
        value: 4,
        requires_subscription: true,
        time: 60
      },
      {
        description: '7y',
        value: 5,
        requires_subscription: true,
        time: 84
      },
      {
        description: '10y',
        value: 6,
        requires_subscription: true,
        time: 120
      },
      {
        description: 'Max',
        value: 0,
        requires_subscription: true,
        time: 0
      }
    ],
    chart: {
      xAxis: null,
      comparisonIndex: null
    },
    chartIntervalItems: [
      {
        value: 'yearly_entries',
        description: 'Yearly'
      },
      {
        value: 'quarterly_entries',
        description: 'Quarterly'
      },
      {
        value: 'monthly_entries',
        description: 'Monthly'
      }
    ],
    chartData: [],
    indices: [],
    maToAdd: null,
    moving_averages: [],
    starting_record: null,
    hedged_matrix: null,
    ending_hedged_transaction: null,
    is_compound_chart: true,
    backtestTableHeaders: [
      { text: '', value: 'name', sortable: false },
      { text: 'YTD', value: 'YTD', sortable: false },
      { text: '1Y', value: '1Y', sortable: false },
      { text: '2Y', value: '2Y', sortable: false },
      { text: '3Y', value: '3Y', sortable: false },
      { text: '5Y', value: '5Y', sortable: false },
      { text: '7Y', value: '7Y', sortable: false },
      { text: '10Y', value: '10Y', sortable: false },
      { text: 'Total Return', value: 'Total Return', sortable: false },
      { text: 'Annualized', value: 'Annualized', sortable: false },
      { text: 'Annualized 3-Yr', value: 'performance_three_year_annualized', sortable: false },
      { text: 'Annualized 5-Yr', value: 'performance_five_year_annualized', sortable: false },
      { text: 'Std Dev', value: 'Std Dev', sortable: false },
      { text: 'Sortino', value: 'Sortino', sortable: false }
    ],
    stocksTableHeaders: [
      { text: 'Stock Name', value: 'Stock Name', sortable: false },
      { text: '% of Portfolio', value: '% of Portfolio', sortable: false },
      { text: 'Total Return', value: 'Total Return', sortable: false },
      { text: 'Original Purchase Date', value: 'Original Purchase Date', sortable: false }
    ]
  }),

  computed: {
    ...mapState({
      criteria: state => state.app.backtesterCriteria,
      user: state => state.user.user
    }),
    ...mapFields({
      criteria: 'app.backtesterCriteria'
    }),

    filteredTransactions () {
      const transactions = this.results.ending_period.transactions
      if (!transactions) return []
      return transactions
        .filter(t => !t.is_sold)
        .map(t => Object.defineProperty(t, 'rowType', { value: 'default' }))
    },

    backtestTableItems () {
      return [
        {
          'name': this.hedged_matrix ? 'Unhedged Portfolio' : 'Whale(s)',
          'YTD': last(this.starting_record.yearly_entries)[3] * 100,
          '1Y': this.GetYearlyPerformance(this.starting_record, 1),
          '2Y': this.GetYearlyPerformance(this.starting_record, 2),
          '3Y': this.GetYearlyPerformance(this.starting_record, 3),
          '5Y': this.GetYearlyPerformance(this.starting_record, 5),
          '7Y': this.GetYearlyPerformance(this.starting_record, 7),
          '10Y': this.GetYearlyPerformance(this.starting_record, 10),
          'Total Return': this.starting_record.performance * 100,
          'Annualized': this.starting_record.annualized_performance * 100,
          'performance_three_year_annualized': this.starting_record.performance_three_year_annualized * 100,
          'performance_five_year_annualized': this.starting_record.performance_five_year_annualized * 100,
          'Std Dev': this.starting_record.standard_deviation * 100.0,
          'Sortino': this.starting_record.sortino
        },
        // Conditionally insert the 'Hedged Portfolio' row
        ...(this.hedged_matrix ? [{
          'name': 'Hedged Portfolio',
          'YTD': last(this.hedged_matrix.yearly_entries)[3] * 100,
          '1Y': this.GetYearlyPerformance(this.hedged_matrix, 1),
          '2Y': this.GetYearlyPerformance(this.hedged_matrix, 2),
          '3Y': this.GetYearlyPerformance(this.hedged_matrix, 3),
          '5Y': this.GetYearlyPerformance(this.hedged_matrix, 5),
          '7Y': this.GetYearlyPerformance(this.hedged_matrix, 7),
          '10Y': this.GetYearlyPerformance(this.hedged_matrix, 10),
          'Total Return': this.hedged_matrix.performance * 100,
          'Annualized': this.hedged_matrix.annualized_performance * 100,
          'performance_three_year_annualized': this.hedged_matrix.performance_three_year_annualized * 100,
          'performance_five_year_annualized': this.hedged_matrix.performance_five_year_annualized * 100,
          'Std Dev': this.hedged_matrix.standard_deviation * 100.0,
          'Sortino': this.hedged_matrix.sortino
        }] : []),
        {
          'name': this.chart.comparisonIndex.description,
          'YTD': last(this.chart.comparisonIndex.yearly_entries)[3] * 100,
          '1Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 1),
          '2Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 2),
          '3Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 3),
          '5Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 5),
          '7Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 7),
          '10Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 10),
          'Total Return': this.chart.comparisonIndex.performance * 100,
          'Annualized': this.chart.comparisonIndex.annualized_performance * 100,
          'performance_three_year_annualized': this.chart.comparisonIndex.performance_three_year_annualized * 100,
          'performance_five_year_annualized': this.chart.comparisonIndex.performance_five_year_annualized * 100,
          'Std Dev': this.chart.comparisonIndex.standard_deviation * 100.0,
          'Sortino': this.chart.comparisonIndex.sortino
        }
      ]
    },

    stocksTableItems () {
      return [
        ...this.filteredTransactions,
        ...(this.results.ending_period.hedge
          ? [{ rowType: 'endingPeriodHedge' }]
          : []
        ),
        ...(this.ending_hedged_transaction && !this.ending_hedged_transaction.is_sold
          ? [{ rowType: 'endingHedgedTransaction' }]
          : []
        )
      ]
    }
  },

  watch: {
    trackingStartDialog (isVisible) {
      if (isVisible) {
        this.$nextTick(() => this.$refs.backtestDescriptionField.focus())
      }
    }
  },

  methods: {
    genResults (results) {
      this.results = results
      this.indices = this.results.performance_matrices.slice(1) // slice removes 'Whales' entry

      if (this.results.performance_matrices.length > 0) {
        this.starting_record = this.results.performance_matrices[0]
      }
      if (this.results.performance_matrices.length > 1) {
        this.chart.comparisonIndex = this.results.performance_matrices[1]
      }
      if (last(this.results.performance_matrices).description === 'Hedged Portfolio') {
        this.hedged_matrix = last(this.results.performance_matrices)
        this.results.performance_matrices[0].description = 'Unhedged Portfolio'
        this.ending_hedged_transaction = last(this.hedged_matrix.monthly_entries).hedge_transaction || null

        const months = this.criteria.hedging_moving_average_months
        const found = this.moving_averages.some(ma => ma.months === months)

        if (!found) {
          this.moving_averages.push({
            description: `Past ${months} months Perf`,
            months
          })
        }
      } else this.hedged_matrix = null

      this.timeframe = this.timeframeItems[7]
      this.chart.xAxis = this.chartIntervalItems[2]

      this.$nextTick(() => {
        this.refresh()
        this.loadSectorChart(
          'backtest_sector_chart',
          this.results.ending_period.transactions
        )
      })
    },

    refresh () {
      this.chartData = []
      let max = 0
      const timeframe = this.timeframe.time
      const today = new Date()
      let earliestDate = new Date(today.getFullYear(), today.getMonth() - timeframe)

      if (this.timeframe.value === 1) {
        earliestDate = new Date(today.getFullYear(), 0, 1)
      }

      const hedgingHighlight = {
        name: 'Hedging is on',
        color: '#FFFF66',
        area: true,
        data: [],
        tooltip: false,
        interactive: false,
        useInteractiveGuideline: false,
        lineinteractive: false
      } // https://djaodjin.com/blog/two-background-colors-nvd3-line-chart.blog.html
      let maxY = 0
      // or bar chart - create new entry see http://nvd3.org/examples/linePlusBar.html
      const a = []

      if (this.results.performance_matrices.length > 1) {
        a.push(this.results.performance_matrices[0])
        if (this.hedged_matrix) {
          a.push(this.hedged_matrix)
          a[0].description = 'Unhedged Portfolio'
        }

        a.push(this.chart.comparisonIndex)
      }
      if (this.chart.xAxis.value === 'monthly_entries') {
        const colors = [
          '#FFCC0F',
          '#C79F25',
          '#705A11',
          '#8BA9D0',
          '#6A90C1',
          '#066CA9',
          '#004B8C'
        ]
        for (
          let movingindex = 0;
          movingindex < this.moving_averages.length;
          movingindex++
        ) {
          const ma = this.moving_averages[movingindex]
          ma.data = []
          ma.name = ma.description
          if (movingindex < colors.length) ma.color = colors[movingindex]
        }
      }
      for (let i = 0; i < a.length; i++) {
        const p = a[i]
        if (p.months) {
          continue
        }
        let data = []
        let tempdata = []
        let last = null
        if (this.chart.xAxis.value === 'quarterly_entries') {
          tempdata = p['monthly_entries']
        } else tempdata = p[this.chart.xAxis.value] // yearly_entries, monthly_entries, etc
        if (timeframe !== 0) {
          // strip out data earlier than earliestDate
          for (let j = 0; j < tempdata.length; j++) {
            const entry = tempdata[j]
            let darray = null
            if (entry.ending_balance) {
              darray = entry.end_date.split('-')
            } else {
              darray = entry[1].split('-')
            }
            const s = new Date(darray[0], darray[1] - 1, darray[2])
            if (s.getTime() >= earliestDate.getTime()) {
              data.push(entry)
            } else last = entry
          }
        } else {
          data = tempdata
        }
        if (this.hedged_matrix && i === 1) {
          tempdata = []
          data.forEach((hentry) => {
            tempdata.push([
              hentry.end_date,
              hentry.end_date,
              hentry.ending_balance,
              hentry.perf_month,
              hentry.hedge_transaction
            ])
          })
          data = tempdata
        }
        const obj = {
          name: p.description,
          data: [],
          area: true
        }
        if (i === 0) {
          obj.color = '#57aea5' // '#96d8cc'
        } else if (i === 1 && this.hedged_matrix) {
          obj.color = '#00cc00'
        } else {
          obj.color = '#111'
        }
        let compound = 0
        let start = p.starting_balance
        if (timeframe !== 0) {
          if (last) start = last[2]
          else start = data[0][2]
        }
        for (let j = 0; j < data.length; j++) {
          const entry = data[j]
          const d3data = {
            x: null,
            y: null,
            hedged: false
          }
          let s = entry[0]
          if (!isNaN(s) || s.length === 4) s = new Date(parseInt(s), 1, 1)
          else {
            const darray = s.split('-')
            s = new Date(darray[0], parseInt(darray[1]) - 1, darray[2])
          }
          d3data.x = s.getTime()
          if (entry.length === 5 && entry[4]) {
            d3data.hedged = true
          }

          if (this.is_compound_chart) {
            compound = ((entry[2] - start) / start) * 1.0
            d3data.y = compound
          } else d3data.y = entry[3]
          obj.data.push(d3data)
          if (d3data.y > max) max = d3data.y
          if (d3data.y > maxY) maxY = d3data.y

          if (i === 0 && this.chart.xAxis.value === 'monthly_entries') {
            for (
              let movingindex = 0;
              movingindex < this.moving_averages.length;
              movingindex++
            ) {
              const ma = this.moving_averages[movingindex]
              // ma will be obj with description and months, values
              // take average of current-ma.months and add to ma.values
              if (j >= ma.months) {
                // whale ma needs to be perf for past X months
                const base = data[j - ma.months][2]
                const maperf = (data[j][2] - base) / base
                // hedging comp ma needs to be avg of sums of perfs for past 12 monts
                // with base being mv from 12 months ago
                ma.data.push({
                  x: d3data.x,
                  y: maperf
                })
              } else {
                ma.data.push({
                  x: d3data.x,
                  y: null
                })
              }
            }
          }

          if (this.chart.xAxis.value === 'quarterly_entries') {
            j += 2
            if (j + 1 >= data.length) j = data.length - 1
          }
        }

        this.chartData.push(obj)
      }

      if (this.chart.xAxis.value === 'monthly_entries') {
        for (
          let movingindex = 0;
          movingindex < this.moving_averages.length;
          movingindex++
        ) {
          const ma = this.moving_averages[movingindex]
          this.chartData.push(ma)
        }
        if (
          this.hedged_matrix &&
          this.results.performance_matrices[0].moving_averages.length > 0
        ) {
          const ma = {
            data: [],
            name: 'Hedging Moving Avg'
          }
          const hedgingMa = this.results.performance_matrices[0].moving_averages
          const compData = this.results.performance_matrices[0].monthly_entries
          const diff = compData.length - hedgingMa.length
          hedgingHighlight.data = []
          for (let i = 0; i < compData.length; i++) {
            const entry = compData[i]
            let s = entry[0]
            if (!isNaN(s) || s.length === 4) s = new Date(parseInt(s), 1, 1)
            else {
              const darray = s.split('-')
              s = new Date(darray[0], parseInt(darray[1]) - 1, darray[2])
            }
            const x = s.getTime()
            if (i >= diff) {
              const maentry = hedgingMa[i - diff]
              ma.data.push({
                x,
                y: maentry.value
              })
              const base = compData[i - diff][2]
              const entryMaPerf = (entry[2] - base) / base
              if (maentry.value > entryMaPerf) {
                hedgingHighlight.data.push({
                  x,
                  y: max
                })
              } else {
                hedgingHighlight.data.push({
                  x,
                  y: null
                })
              }
            } else {
              ma.data.push({
                x,
                y: null
              })
              hedgingHighlight.data.push({
                x,
                y: null
              })
            }
          }
          this.chartData.push(ma)
          this.chartData.push(hedgingHighlight)
          // modify for highcharts here
        }
      }

      Highcharts.chart('backtest_chart', {
        chart: {
          type: 'area',
          backgroundColor: '#FFFFFF'
        },
        title: {
          text: ''
        },
        subtitle: {},
        xAxis: {
          allowDecimals: false,
          labels: {
            formatter () {
              const d = new Date(this.value)
              if (this.chart.xAxis.value === 'yearly_entries') return d.getFullYear()
              else return d.getFullYear() + '-' + (d.getMonth() + 1) // d3.time.format('%Y-%m')(new Date(d));
              // return this.value; // clean, unformatted number for year
            }
          }
        },
        yAxis: {
          title: {
            text: ''
          },
          labels: {
            formatter () {
              return this.value * 100.0 + '%'
            }
          }
        },
        tooltip: {
          shared: true,
          formatter () {
            const points = this.points
            const pointsLength = points.length
            const d = new Date(points[0].key)

            let tooltipMarkup = pointsLength
              ? '<span style="font-size: 10px">' +
                d.getFullYear() +
                '-' +
                (d.getMonth() + 1) +
                '</span><br/>'
              : ''
            let index
            let yValueKwh

            for (index = 0; index < pointsLength; index += 1) {
              yValueKwh = (points[index].y * 100.0).toFixed(2)

              tooltipMarkup +=
                '<span style="color:' +
                points[index].series.color +
                '">\u25CF</span> ' +
                points[index].series.name +
                ': <b>' +
                yValueKwh +
                '%</b><br/>'
            }

            return tooltipMarkup
          }
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          area: {
            // pointStart: 1940,
            marker: {
              enabled: false,
              symbol: 'circle',
              radius: 2,
              states: {
                hover: {
                  enabled: true
                }
              }
            }
          }
        },
        series: this.chartData
      })
    },

    UpdateBacktestTracking (backtestTrackingId) {
      this.loadingTracking = true
      api.post('save_tracked_backtest_changes', this.criteria)
        .then(response => {
          this.$store.dispatch('user/loadTrackedBacktests')
          this.criteria.backtest_tracking_id = parseInt(response.data)
          this.trackingStartDialog = false
        })
        .catch(error => {
          this.$store.commit('app/NOTIFICATION', { type: 'error', text: (error.response && error.response.data) || error.message })
        })
        .then(() => (this.loadingTracking = false))
    },

    trackingStart () {
      if (this.$refs.trackingForm.validate()) {
        this.loadingTracking = true
        this.criteria.backtest_tracking_id = null
        api.post('track_backtest', this.criteria)
          .then(response => {
            this.$store.dispatch('user/loadTrackedBacktests')
            this.criteria.backtest_tracking_id = parseInt(response.data)
            this.trackingStartDialog = false
          })
          .catch(error => {
            this.$store.commit('app/NOTIFICATION', { type: 'error', text: (error.response && error.response.data) || error.message })
          })
          .then(() => (this.loadingTracking = false))
      }
    },

    cancelTrackingStart () {
      this.trackingStartDialog = false
      this.$refs.trackingForm.reset()
    },

    trackingStop () {
      this.loadingTracking = true

      api.get(`stop_tracking_backtest?id=${this.criteria.backtest_tracking_id}`)
        .then(response => {
          this.$store.dispatch('user/loadTrackedBacktests')
          this.criteria.backtest_tracking_id = null
          this.criteria.backtest_tracking_description = null
          this.trackingStopDialog = false
        })
        .catch(error => {
          this.$store.commit('app/NOTIFICATION', { type: 'error', text: (error.response && error.response.data) || error.message })
        })
        .then(() => (this.loadingTracking = false))
    },

    loadSectorChart (element, data) {
      const chartSeries = []
      const series = {
        name: 'Sectors',
        data: []
      }
      const h = {}
      let totalMV = 0

      for (let i = 0; i < data.length; i++) {
        const stock = data[i]
        totalMV += stock.shares * stock.price_on_rebalancing_date
      }

      for (let i = 0; i < data.length; i++) {
        const stock = data[i]
        if (stock.shares === 0) continue
        if (!h[stock.sector]) {
          h[stock.sector] = {
            name: stock.sector,
            y: 0
          }
        }
        h[stock.sector].y += stock.shares * stock.price_on_rebalancing_date
      }

      for (const propertyName in h) {
        if (propertyName === undefined) continue
        const entry = h[propertyName]
        if (!entry.y) continue
        entry.y = (entry.y / totalMV) * 100.0
        series.data.push(entry)
      }

      chartSeries.push(series)

      Highcharts.chart(element, {
        chart: {
          type: 'pie',
          backgroundColor: '#FFFFFF'
        },
        legend: {
          itemStyle: {
            color: '#000000',
            fontWeight: 'bold'
          },
          itemHoverStyle: {
            color: '#333'
          }
        },
        title: {
          text: 'Sector Allocation'
        },
        xAxis: {
          type: 'category'
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Sector Percentage'
          }
        },
        tooltip: {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>:  ({point.percentage:.0f}%)<br/>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: false
            },
            showInLegend: true
          }
        },
        credits: {
          enabled: false
        },
        series: chartSeries
      })
    },

    setTimeframe (timeframe) {
      this.timeframeProxy = timeframe.value
      this.refresh()
    },

    GetYearlyPerformance (record, numYears) {
      if (!record) return ''
      let perf = 0
      let start = 0
      let end = 0
      const numMonths = numYears * 12
      const months = record.monthly_entries
      if (months.length <= numMonths) return 'N/A'
      if (months[months.length - numMonths - 1].ending_balance) {
        start = months[months.length - numMonths - 1].ending_balance
        end = months[months.length - 1].ending_balance
      } else {
        start = months[months.length - numMonths - 1][2]
        end = months[months.length - 1][2]
      }
      perf = (end - start) / start
      return perf * 100.0
    },

    GetTransactionTotalReturn (transaction) {
      if (!transaction) return ''
      const basis = transaction.avg_price * transaction.shares
      const sp = transaction.current_stock_price
        ? transaction.current_stock_price
        : transaction.price_on_rebalancing_date
      const unrealizedGain = sp * transaction.shares - basis
      const gain = transaction.realized_gain + unrealizedGain

      return (gain / basis) * 100.0
    },

    AddMovingAverage () {
      if (!this.moving_averages.some(ma => ma.months === this.maToAdd)) {
        const sortedArray = this.moving_averages
        sortedArray.push({
          description: `${this.maToAdd} month moving average`,
          months: this.maToAdd
        })
        this.moving_averages = sortedArray.sort((ma1, ma2) => parseInt(ma1.months) > parseInt(ma2.months))
        this.maToAdd = null
      }
    },

    DeleteMovingAverage (ma) {
      const index = this.moving_averages.indexOf(ma)
      if (index >= 0) {
        this.moving_averages.splice(index, 1)
      }
    },

    CloseMovingAverageDialog () {
      this.movingAveragesDialog = false
      this.refresh()
    }
  }
}
</script>

<style lang="scss">
#backtest-table, #backtest-stocks-table {
  thead th {
    color: rgba(0,0,0,0.87);
  }
}
</style>
